import {
  ControlledCheckbox,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import ClaimNotesField from "components/ClaimNotesField";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { usePatchClaimantByIdMutation } from "services/claimant-api";
import { usePatchDateOfInjuryByIdMutation } from "services/doi-api";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  FileType,
  MSADataType,
} from "types/DataTypes";
import { useSelector } from "utils/redux";

import { FEATURE_FLAGS } from "constants/FeatureFlags";
import LibertyMutualReferralFields from "components/LibertyMutualReferralFields";

import { isLibertyMutualFile } from "./future-medical-utils";

type FMOperationsProps = {
  service: MSADataType | CostProjectionDataType | AmendedReviewScreeningType;
};

const FMOperations = ({ service }: FMOperationsProps) => {
  const constants = useSelector((state) => state.shared.constants);
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const [selectedDoiId, setSelectedDoiId] = useState<number | null>(null);
  const [selectedStateDoiId, setSelectedStateDoiId] = useState<number | null>(
    null,
  );
  const [isResetting, setIsResetting] = useState(false);
  const prevSelectedDoiIdRef = useRef<number | null>(null);
  const [patchDateOfInjuryById] = usePatchDateOfInjuryByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();
  const [patchClaimantById] = usePatchClaimantByIdMutation();
  const isReadOnly = !loggedInUser?.has_operations_authority;

  // get the selected service DOI from Redux and its corresponding date_of_injury_id
  const currentServiceDoi = useSelector(
    (state) => state.futureMedical.currentServiceDoi,
  );

  const {
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  // Accept either a nested FileType or a bare file id from the service
  // payload. MSADataType/CostProjectionDataType/AmendedReviewScreeningType
  // all declare `file: FileType`, so the cast widens it back to a real
  // union for this check. Narrowing on `typeof === "number"` rather than
  // `typeof === "object" && != null` because this project's tsconfig has
  // `strict: false` (no strictNullChecks) -- without that, TypeScript
  // doesn't reliably exclude FileType from the object/non-null branch, but
  // narrowing on the primitive case works regardless.
  const serviceFile = service.file as number | FileType;
  const fileId =
    typeof serviceFile === "number" ? serviceFile : serviceFile?.id;

  // fetch file data - this will use cached data if already loaded by Header
  const { data: fileData, isLoading } = useGetFileByIdQuery(
    { id: fileId?.toString() || "" },
    { skip: !fileId },
  );

  // use dates_of_injury directly from fileData (already includes all fields)
  const dois = useMemo(() => fileData?.dates_of_injury || [], [fileData]);

  // select a DOI by ID
  const selectDoi = useCallback((doiId: string | null) => {
    const numericId = doiId ? parseInt(doiId) : null;
    setSelectedDoiId(numericId);
  }, []);

  const handleDoiSave = useCallback(
    async (field: string, value: string) => {
      if (!selectedDoiId) return;

      try {
        await patchDateOfInjuryById({
          id: selectedDoiId.toString(),
          data: {
            [field]: value || "",
          },
        }).unwrap();
      } catch (error) {
        console.error("Error saving DOI field:", error);
      }
    },
    [patchDateOfInjuryById, selectedDoiId],
  );

  const handleFileSave = useCallback(
    async (field: string, value: unknown) => {
      if (!fileId) return;

      try {
        await patchFileById({
          id: fileId.toString(),
          data: {
            [field]: value,
          },
        }).unwrap();
      } catch (error) {
        console.error("Error saving file field:", error);
      }
    },
    [patchFileById, fileId],
  );

  const claimantId = fileData?.claimants?.[0]?.id;

  const handleClaimantSave = useCallback(
    async (field: string, value: unknown) => {
      if (!claimantId) return;

      try {
        await patchClaimantById({
          id: claimantId,
          data: {
            [field]: value,
          },
        }).unwrap();
      } catch (error) {
        console.error("Error saving claimant field:", error);
      }
    },
    [patchClaimantById, claimantId],
  );

  // sync with service DOI selection from header
  useEffect(() => {
    if (currentServiceDoi?.date_of_injury_id) {
      setSelectedDoiId(currentServiceDoi.date_of_injury_id);
    }
  }, [currentServiceDoi]);

  // automatically select first DOI when data loads (only if no service DOI is selected)
  useEffect(() => {
    if (dois.length > 0 && !selectedDoiId && !currentServiceDoi) {
      setSelectedDoiId(dois[0].id);
    }
  }, [dois, selectedDoiId, currentServiceDoi]);

  // update form values when DOI selection changes
  useEffect(() => {
    if (!selectedDoiId || !dois.length) return;

    const doi = dois.find((d) => d.id === selectedDoiId);
    if (!doi) return;

    setIsResetting(true);
    setSelectedStateDoiId(selectedDoiId);

    // check if we're switching DOIs
    const isSwitchingDois = prevSelectedDoiIdRef.current !== selectedDoiId;
    prevSelectedDoiIdRef.current = selectedDoiId;

    // when switching DOIs, directly update jurisdiction fields to prevent stale values
    if (isSwitchingDois) {
      setValue("jurisdiction", doi.jurisdiction || "");
      setValue("wcmsa_zip_code", doi.wcmsa_zip_code || "");
      setValue("one_eleven_jurisdiction", doi.one_eleven_jurisdiction || "");
      setValue("accident_description", doi.accident_description || "");
      setValue("denied_conditions", doi.denied_conditions || "");
      setValue("accepted_conditions", doi.accepted_conditions || "");
      setValue(
        "description_of_claimants_job",
        doi.description_of_claimants_job || "",
      );
    }

    reset(
      {
        // DOI fields
        jurisdiction: doi.jurisdiction || "",
        wcmsa_zip_code: doi.wcmsa_zip_code || "",
        one_eleven_jurisdiction: doi.one_eleven_jurisdiction || "",
        accident_description: doi.accident_description || "",
        denied_conditions: doi.denied_conditions || "",
        accepted_conditions: doi.accepted_conditions || "",
        description_of_claimants_job: doi.description_of_claimants_job || "",
        // File-level fields
        applied_ssd: fileData.applied_ssd || false,
        awarded_ssd: fileData.awarded_ssd || false,
        is_medicare_eligible:
          fileData.claimants?.[0]?.is_medicare_eligible || false,
        referring_party_name: fileData.referring_party_name || "",
        referring_party_email: fileData.referring_party_email || "",
        referring_party_phone: fileData.referring_party_phone || "",
        settlement_reached: fileData.settlement_reached || false,
        estimated_settlement_amount: fileData.estimated_settlement_amount || "",
        estimated_medical_settlement_value:
          fileData.estimated_medical_settlement_value || "",
        reserve_settlement_amount: fileData.reserve_settlement_amount || "",
        tentative_settlement_minimum:
          fileData.tentative_settlement_minimum || "",
        tentative_settlement_maximum:
          fileData.tentative_settlement_maximum || "",
        preferred_structure_settlement_broker:
          fileData.preferred_structure_settlement_broker || "",
        // Liberty Mutual referral fields
        grs_or_legacy_rcg: fileData.grs_or_legacy_rcg || "",
        segmentation_type: fileData.segmentation_type || "",
        rcg_carrier: fileData.rcg_carrier || "",
        referral_type: fileData.referral_type || "",
        wcrc_case_id: fileData.wcrc_case_id || "",
        beneficiary_class: fileData.beneficiary_class || "",
      },
      {
        // only keep dirty values if we're not switching DOIs
        keepDirtyValues: !isSwitchingDois,
      },
    );

    setIsResetting(false);
  }, [selectedDoiId, reset, setValue, dois, fileData]);

  const isLibertyFeatureEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.LIBERTY_REFERRAL_FIELDS_ENABLED],
  );
  const isLiberty = useMemo(
    () => isLibertyFeatureEnabled && isLibertyMutualFile(fileData),
    [isLibertyFeatureEnabled, fileData],
  );

  const options = useMemo(() => {
    return dois.map((doi) => ({
      value: doi.id,
      label: doi.date.substring(0, 10),
    }));
  }, [dois]);

  const selectedOption = useMemo(
    () =>
      options.find((o) => o.value?.toString() === selectedDoiId?.toString()),
    [options, selectedDoiId],
  );

  if (!fileData) {
    return null;
  }

  const dateOfInjuryCard = (
    <FutureMedicalCard heading="Date of Injury Information" cardNumber={3}>
      {isLoading ? (
        <div>Loading DOIs...</div>
      ) : !dois.length ? (
        <div>No dates of injury available</div>
      ) : (
        <Select
          name="doi"
          label="DOI"
          options={options}
          value={selectedOption}
          readOnly={isReadOnly}
          onChange={(o: OptionType) => selectDoi(o?.value?.toString() || null)}
        />
      )}

      <ControlledSelect
        key={`jurisdiction-${selectedStateDoiId}`}
        name="jurisdiction"
        control={control}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        label="WCMSA State"
        options={constants?.STATES || []}
        errors={errors}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await handleDoiSave("jurisdiction", optionValue);
        }}
      />

      <ControlledInput
        key={`wcmsa-zip-${selectedStateDoiId}`}
        name="wcmsa_zip_code"
        componentType={MaskedInput}
        mask="zipcode-nine"
        control={control}
        label="WCMSA Zip Code"
        errors={errors}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        onSave={(value) => handleDoiSave("wcmsa_zip_code", String(value || ""))}
      />

      <ControlledSelect
        key={`111-jurisdiction-${selectedStateDoiId}`}
        name="one_eleven_jurisdiction"
        control={control}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        label="111 Jurisdiction"
        options={constants?.JURISDICTIONS || []}
        errors={errors}
        onSave={async (option) => {
          const optionValue = option?.value != null ? String(option.value) : "";
          await handleDoiSave("one_eleven_jurisdiction", optionValue);
        }}
      />

      <ControlledTextArea
        key={`accident-description-${selectedStateDoiId}`}
        name="accident_description"
        control={control}
        errors={errors}
        label="Accident Description"
        advancedEdit={true}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        onSave={(value) =>
          handleDoiSave("accident_description", String(value || ""))
        }
      />

      <ControlledTextArea
        key={`job-description-${selectedStateDoiId}`}
        name="description_of_claimants_job"
        control={control}
        errors={errors}
        label="Description of Claimant's Job at time of injury"
        advancedEdit={true}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        onSave={(value) =>
          handleDoiSave("description_of_claimants_job", String(value || ""))
        }
      />

      <ControlledTextArea
        key={`accepted-conditions-${selectedStateDoiId}`}
        name="accepted_conditions"
        control={control}
        errors={errors}
        label="Accepted Conditions"
        advancedEdit={true}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        onSave={(value) =>
          handleDoiSave("accepted_conditions", String(value || ""))
        }
      />

      <ControlledTextArea
        key={`denied-conditions-${selectedStateDoiId}`}
        name="denied_conditions"
        control={control}
        errors={errors}
        label="Denied Conditions"
        advancedEdit={true}
        readOnly={isReadOnly || !selectedDoiId || isResetting}
        onSave={(value) =>
          handleDoiSave("denied_conditions", String(value || ""))
        }
      />
    </FutureMedicalCard>
  );

  return (
    <div className="swim-column">
      <FutureMedicalCard heading="Referral Information" cardNumber={1}>
        <ControlledCheckbox
          control={control}
          name="is_medicare_eligible"
          label="Medicare Eligible"
          errors={errors}
          readOnly={isReadOnly || !claimantId}
          onSave={(value) => handleClaimantSave("is_medicare_eligible", value)}
        />

        <ControlledInput
          control={control}
          name="referring_party_name"
          label="Referring Party Name"
          errors={errors}
          readOnly={isReadOnly}
          componentType={TextInput}
          onSave={(value) =>
            handleFileSave("referring_party_name", String(value || ""))
          }
        />

        <ControlledInput
          control={control}
          name="referring_party_email"
          label="Referring Party Email"
          errors={errors}
          readOnly={isReadOnly}
          componentType={TextInput}
          onSave={(value) =>
            handleFileSave("referring_party_email", String(value || ""))
          }
        />

        <ControlledInput
          control={control}
          name="referring_party_phone"
          label="Referring Party Phone"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="phone"
          onSave={(value) =>
            handleFileSave("referring_party_phone", String(value || ""))
          }
        />

        <ControlledCheckbox
          control={control}
          name="awarded_ssd"
          label="Awarded SSD"
          errors={errors}
          readOnly={isReadOnly}
          onSave={(value) => handleFileSave("awarded_ssd", value)}
        />

        <ControlledCheckbox
          control={control}
          name="applied_ssd"
          label="Applied SSD"
          errors={errors}
          readOnly={isReadOnly}
          onSave={(value) => handleFileSave("applied_ssd", value)}
        />
      </FutureMedicalCard>

      <FutureMedicalCard heading="Settlement Information" cardNumber={2}>
        <ControlledCheckbox
          control={control}
          name="settlement_reached"
          label="Settlement Reached"
          errors={errors}
          readOnly={isReadOnly}
          onSave={(value) => handleFileSave("settlement_reached", value)}
        />

        <ControlledInput
          control={control}
          name="estimated_settlement_amount"
          label="Estimated Settlement Amount"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="dollar"
          onSave={(value) =>
            handleFileSave("estimated_settlement_amount", value || null)
          }
        />

        <ControlledInput
          control={control}
          name="estimated_medical_settlement_value"
          label="Estimated Medical Settlement Value"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="dollar"
          onSave={(value) =>
            handleFileSave("estimated_medical_settlement_value", value || null)
          }
        />

        <ControlledInput
          control={control}
          name="reserve_settlement_amount"
          label="Settlement Amount"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="dollar"
          onSave={(value) =>
            handleFileSave("reserve_settlement_amount", value || null)
          }
        />

        <ControlledInput
          control={control}
          name="tentative_settlement_minimum"
          label="Tentative Settlement Minimum"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="dollar"
          onSave={(value) =>
            handleFileSave("tentative_settlement_minimum", value || null)
          }
        />

        <ControlledInput
          control={control}
          name="tentative_settlement_maximum"
          label="Tentative Settlement Maximum"
          errors={errors}
          readOnly={isReadOnly}
          componentType={MaskedInput}
          mask="dollar"
          onSave={(value) =>
            handleFileSave("tentative_settlement_maximum", value || null)
          }
        />

        <ControlledInput
          control={control}
          name="preferred_structure_settlement_broker"
          label="Preferred Structure Settlement Broker"
          errors={errors}
          readOnly={isReadOnly}
          componentType={TextInput}
          onSave={(value) =>
            handleFileSave(
              "preferred_structure_settlement_broker",
              String(value || ""),
            )
          }
        />
      </FutureMedicalCard>

      {dateOfInjuryCard}

      {isLiberty && (
        <FutureMedicalCard
          heading="Liberty Mutual Referral Information"
          cardNumber={4}
        >
          <LibertyMutualReferralFields
            control={control}
            errors={errors}
            readOnly={isReadOnly}
            showBeneficiaryClass
            onFieldSave={handleFileSave}
          />
        </FutureMedicalCard>
      )}

      {isLiberty && (
        <ClaimNotesField
          fileId={fileId}
          readOnly={isReadOnly}
          variant="future-medical"
          cardNumber={4}
        />
      )}
    </div>
  );
};

export default FMOperations;
